import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, AlertOctagon, Clock, FileWarning, Users as UsersIcon,
  ChevronRight, Search, Star, FileX, Inbox, FileCheck2, Network, Briefcase,
} from "lucide-react";

const TABS = [
  { id: "new_intakes",            label: "New Intakes",           icon: Inbox },
  { id: "kyc_pending",            label: "KYC Pending",           icon: Clock },
  { id: "kyc_complete",           label: "KYC Complete",          icon: ShieldCheck },
  { id: "accreditation_pending",  label: "Accreditation Pending", icon: Clock },
  { id: "ready_for_partner",      label: "Ready for Partner",     icon: Briefcase },
  { id: "partner_complete",       label: "Partner Onboarded",     icon: FileCheck2 },
  { id: "red_alert",              label: "Red Alert",             icon: ShieldAlert, danger: true },
  { id: "compliance",             label: "Compliance",            icon: FileWarning, warn: true },
  { id: "introducer_review",      label: "Introducer Review",     icon: Network },
  { id: "high_value",             label: "High Value",            icon: Star },
  { id: "stalled",                label: "Stalled",               icon: Clock, warn: true },
  { id: "reopen_requested",       label: "Reopen Requested",      icon: Inbox, warn: true },
  { id: "closed",                 label: "Closed / Rejected",     icon: FileX },
];

const STATUS_PILL = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("red alert"))          return "bg-red-100 text-red-900 border-red-300";
  if (s.includes("compliance"))         return "bg-red-50 text-red-900 border-red-200";
  if (s.includes("priority"))           return "bg-amber-100 text-amber-900 border-amber-300";
  if (s.includes("rejected") || s.includes("closed")) return "bg-slate-200 text-slate-700 border-slate-300";
  if (s.includes("complete") || s.includes("ready"))  return "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (s.includes("pending"))            return "bg-blue-100 text-blue-900 border-blue-300";
  return "bg-slate-100 text-slate-700 border-slate-300";
};

const AdminCommandCenterPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("new_intakes");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Read search via a ref inside `load` so that typing in the search box
  // doesn't cause the effect below to re-fire on every keystroke. The
  // existing Refresh button is the explicit search-apply trigger.
  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/command-center/admin/files", { params: { tab, search: searchRef.current, limit: 300 } });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load files");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const counts = data?.tab_counts || {};

  return (
    <DashboardLayout title="Command Center" subtitle="EchoSense onboarding operating system">
      {/* TAB STRIP */}
      <div data-testid="admin-cc-tabs" className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          const count = counts[t.id] ?? 0;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : t.danger
                  ? "bg-white text-red-700 border-red-300 hover:bg-red-50"
                  : t.warn
                  ? "bg-white text-amber-800 border-amber-300 hover:bg-amber-50"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <Icon size={14} />
              {t.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${active ? "bg-white/20" : "bg-slate-100 text-slate-700"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* SEARCH */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            data-testid="admin-cc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search VC#, email, name..."
            className="pl-9 bg-white text-slate-900 border-slate-300"
          />
        </div>
        <Button onClick={load} variant="outline" className="border-slate-300">Refresh</Button>
      </div>

      {/* TABLE */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 text-base">
            {TABS.find((t) => t.id === tab)?.label} · {data?.count ?? 0} files
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading...</div>
          ) : data?.rows?.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No files in this tab.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left p-3">ES-VC#</th>
                    <th className="text-left p-3">Member</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Priority</th>
                    <th className="text-left p-3">Responsible</th>
                    <th className="text-left p-3">Flags</th>
                    <th className="text-left p-3">Updated</th>
                    <th className="text-right p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs text-slate-900">{r.vc_number}</td>
                      <td className="p-3 text-slate-900">
                        <div className="font-medium">{r.full_name || "—"}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded border text-xs ${STATUS_PILL(r.status)}`}>{r.status}</span>
                      </td>
                      <td className="p-3 text-xs uppercase">{r.priority_level}</td>
                      <td className="p-3 text-xs">{r.responsible_party}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {r.red_alert && <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">Red</Badge>}
                          {r.compliance_attention_required && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">Comp</Badge>}
                          {r.is_priority_stalled && <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">P-Stalled</Badge>}
                          {!r.is_priority_stalled && r.is_stalled && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">Stalled</Badge>}
                          {r.is_high_value && <Badge className="bg-violet-100 text-violet-800 border-violet-300 text-xs">HV</Badge>}
                          {r.introducer_compliance_review && <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-xs">Intro</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-slate-500">{(r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-3 text-right">
                        <Button
                          data-testid={`open-file-${r.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/command-center/${r.user_id}`)}
                          className="border-slate-300"
                        >
                          Open <ChevronRight size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default AdminCommandCenterPage;
